import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchProjects } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import Highlight from './KernelHighlight';
import { searchTerms } from '../utils/kernelSearch';

/**
 * Ricerca globale su progetti e attività (Ctrl+K / Cmd+K, oppure il pulsante
 * "Cerca" nella pagina Progetti). Frecce per scegliere, Invio per aprire,
 * Esc per chiudere.
 */
function GlobalSearch() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const terms = searchTerms(query);
  const [results, setResults] = useState({ projects: [], tasks: [] });
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('open-global-search', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('open-global-search', onOpen);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults({ projects: [], tasks: [] });
      setActive(0);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    }
  }, [open]);

  // Ricerca con un piccolo ritardo mentre si scrive.
  useEffect(() => {
    if (!open || query.trim().length < 2) {
      setResults({ projects: [], tasks: [] });
      return undefined;
    }
    const timer = setTimeout(() => {
      searchProjects(query.trim())
        .then((data) => {
          setResults({ projects: data.projects || [], tasks: data.tasks || [] });
          setActive(0);
        })
        .catch(() => setResults({ projects: [], tasks: [] }));
    }, 220);
    return () => clearTimeout(timer);
  }, [query, open]);

  if (!open) return null;

  const items = [
    ...results.projects.map((p) => ({ key: `p${p.id}`, icon: p.icon || '📋', title: p.name, sub: p.archived ? t('globalSearch.archived') : '', path: `/projects/${p.id}` })),
    ...results.tasks.map((task) => ({ key: `t${task.id}`, icon: task.project_icon || '📋', title: task.title || t('projectDetail.untitled'), sub: task.project_name, path: `/projects/${task.project_id}?task=${task.id}` })),
  ];

  const go = (item) => {
    setOpen(false);
    navigate(item.path);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') setOpen(false);
    else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && items[active]) {
      go(items[active]);
    }
  };

  return (
    <>
      <div className="pd-modal-overlay" onClick={() => setOpen(false)} />
      <div className="pd-modal gs-panel" role="dialog" aria-label={t('globalSearch.title')} onKeyDown={onKeyDown}>
        <input
          ref={inputRef}
          className="gs-input"
          value={query}
          placeholder={t('globalSearch.placeholder')}
          aria-label={t('globalSearch.placeholder')}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="gs-results">
          {query.trim().length >= 2 && items.length === 0 && <p className="nt-muted gs-empty">{t('globalSearch.noResults')}</p>}
          {query.trim().length < 2 && <p className="nt-muted gs-empty">{t('globalSearch.hint')}</p>}
          {results.projects.length > 0 && <div className="pd-popover-label gs-group">{t('globalSearch.projects')}</div>}
          {items.filter((i) => i.key.startsWith('p')).map((item) => (
            <button key={item.key} type="button" className={'gs-item' + (items[active] === item ? ' gs-item-active' : '')} onMouseEnter={() => setActive(items.indexOf(item))} onClick={() => go(item)}>
              <span aria-hidden="true">{item.icon}</span> <span className="gs-title"><Highlight text={item.title} terms={terms} /></span> <span className="nt-muted"><Highlight text={item.sub} terms={terms} /></span>
            </button>
          ))}
          {results.tasks.length > 0 && <div className="pd-popover-label gs-group">{t('globalSearch.tasks')}</div>}
          {items.filter((i) => i.key.startsWith('t')).map((item) => (
            <button key={item.key} type="button" className={'gs-item' + (items[active] === item ? ' gs-item-active' : '')} onMouseEnter={() => setActive(items.indexOf(item))} onClick={() => go(item)}>
              <span aria-hidden="true">{item.icon}</span> <span className="gs-title"><Highlight text={item.title} terms={terms} /></span> <span className="nt-muted"><Highlight text={item.sub} terms={terms} /></span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export default GlobalSearch;
